/**
 * Flow tag condition row — renders a ranking editor or a metric editor.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';
import { ArrowLeftRight, Trash2 } from 'lucide-react';
import type { FlowLayer, FlowTagCondition } from '@trafficpulse/shared';

import { Button, NumberField } from '@/components/ui';
import type { TagRuleScope } from '@/features/tag-rule-scope';
import { ConditionRow } from '@/pages/admin/ListTagRulesPage/components/rule-condition';

import { FlowCompareConditionRow, defaultCompareTo } from './compare';
import { toMetricCondition } from './flow-metric-condition';

import {
  RANKING_METRIC_OPTIONS,
  RANKING_OP_OPTIONS,
  RANKING_SCOPE_OPTIONS,
} from './flow-tag-format';

interface FlowTagConditionRowProps {
  condition: FlowTagCondition;
  layer: FlowLayer;
  onChange: (c: FlowTagCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
  /**
   * Country/campaign being edited. Named `overrideScope`, not `scope`, because a
   * RANKING condition already owns a `scope` field meaning its comparison pool —
   * two unrelated ideas that would otherwise share a name on the same row.
   * Ranking conditions have no thresholds, so this only affects metric ones.
   */
  overrideScope?: TagRuleScope;
}

const SELECT_CLASS =
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export const FlowTagConditionRow: FC<FlowTagConditionRowProps> = ({
  condition,
  layer,
  onChange,
  onRemove,
  canRemove,
  overrideScope,
}) => {
  if (condition.kind === 'metric') {
    if (condition.compareTo) {
      // Compare mode bypasses the shared row entirely: the RHS metric IS the
      // threshold, so the value input and per-scope overrides don't apply.
      return (
        <FlowCompareConditionRow
          condition={condition}
          onChange={onChange}
          onRemove={onRemove}
          canRemove={canRemove}
        />
      );
    }
    const metric = toMetricCondition(condition);
    return (
      <ConditionRow
        condition={metric}
        // Spread the ORIGINAL first: ConditionRow only knows the perf-tag
        // shape, so fields it never heard of (compareTo & future ones)
        // must survive its write-backs; its explicit-undefined clears win.
        onChange={(c) => onChange({ ...condition, ...c, kind: 'metric' })}
        onRemove={onRemove}
        canRemove={canRemove}
        scope={overrideScope}
        action={
          // Same ⇄ affordance as the drawer rows — swaps the fixed value for
          // another metric (overrides don't apply there, so they're cleared).
          <button
            type="button"
            onClick={() =>
              // Overrides stay: each row independently pins a number or
              // carries its own comparison.
              onChange({ ...condition, compareTo: defaultCompareTo(condition.field) })
            }
            title="Compare to another metric instead of a fixed value"
            aria-label="Compare to a metric instead of a fixed value"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        }
      />
    );
  }

  const needsPercent =
    RANKING_OP_OPTIONS.find((o) => o.op === condition.op)?.needsPercent ?? false;
  const metricOption = RANKING_METRIC_OPTIONS.find((o) => o.key === condition.metric);

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
      <div className="min-w-[130px] flex-1">
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Rank by</label>
        <select
          value={condition.metric}
          onChange={(e) => onChange({ ...condition, metric: e.target.value as never })}
          className={SELECT_CLASS}
          aria-label="Ranking metric"
        >
          {RANKING_METRIC_OPTIONS.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="min-w-[120px]">
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Ranking</label>
        <select
          value={condition.op}
          onChange={(e) => onChange({ ...condition, op: e.target.value as never })}
          className={SELECT_CLASS}
          aria-label="Ranking operator"
        >
          {RANKING_OP_OPTIONS.map((o) => (
            <option key={o.op} value={o.op}>{o.label}</option>
          ))}
        </select>
      </div>
      {needsPercent && (
        <div className="w-[88px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Percent</label>
          <NumberField
            value={condition.percent ?? 10}
            min={1}
            max={100}
            onChange={(percent) => onChange({ ...condition, percent })}
            aria-label="Ranking percent"
            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}
      {layer !== 'paths' && (
        <div className="min-w-[120px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Pool</label>
          <select
            value={condition.scope ?? 'path'}
            onChange={(e) => onChange({ ...condition, scope: e.target.value as never })}
            className={SELECT_CLASS}
            aria-label="Ranking scope"
          >
            {RANKING_SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}
      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="h-9 w-9 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label="Remove condition"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      {metricOption && (
        // Plain-English meaning of the chosen "Rank by" metric, updates as you switch.
        <p className="w-full text-xs leading-snug text-muted-foreground">
          <span className="font-medium text-foreground">{metricOption.label}:</span>{' '}
          {metricOption.hint}
        </p>
      )}
    </div>
  );
};
